import React, { useCallback, useState } from 'react'
import ReactFocusLock from 'react-focus-lock'
import { l, Modal } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import {
  AiAlwaysOnAutomationsActions,
  AiAlwaysOnAutomationsSelectors,
} from 'apps/aiAutomation/store/core'
import { LoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationScenario } from 'shared/api/requests/aiAutomation/schemas'

interface DisableScenarioConfirmProps {
  scenario: AiAutomationScenario
  source: Parameters<typeof AiAutomationAnalytics.logAutomationStatusChanged>[0]['source']
  onClose: () => void
}

export const DisableScenarioConfirm = ({
  scenario,
  source,
  onClose,
}: DisableScenarioConfirmProps) => {
  const dispatch = useAppDispatch()

  const loaderStatus = useAppSelector(AiAlwaysOnAutomationsSelectors.getLoaderStatus)

  const disableAutomation = async () => {
    await dispatch(
      AiAlwaysOnAutomationsActions.updateScenarioStatus({
        scenario,
        status: false,
      }),
    ).unwrap()

    AiAutomationAnalytics.logAutomationStatusChanged({
      type: scenario,
      is_published: false,
      source,
    })

    onClose()
  }

  const isLoading =
    scenario === AiAutomationScenario.INSTANT_ANSWERS
      ? loaderStatus === LoaderStatus.INSTANT_ANSWERS_STATUS_UPDATING
      : loaderStatus === LoaderStatus.SOCIAL_ENGAGEMENT_STATUS_UPDATING

  return (
    <ReactFocusLock>
      <Modal
        width={460}
        open
        onRequestClose={onClose}
        buttons={[
          {
            onClick: onClose,
            label: l.translate('Keep automations on'),
          },
          {
            variant: 'danger',
            label: l.translate('Stop automations'),
            onClick: disableAutomation,
            loading: isLoading,
            'data-test-id': 'ai-automation/confirm-disabling-last-scenario',
          },
        ]}
        title={l.translate('AI goals will be turned off')}
      >
        <p className="p-x-xs p-y">
          {l.translate(
            'You’re stopping automations. If you continue, your AI goals will be turned off too.',
          )}
        </p>
      </Modal>
    </ReactFocusLock>
  )
}

export const useAutomationStatusToggle = ({
  scenario,
  source,
}: {
  scenario: AiAutomationScenario
  source: Parameters<typeof AiAutomationAnalytics.logAutomationStatusChanged>[0]['source']
}) => {
  const dispatch = useAppDispatch()

  const socialEngagementPublished = useAppSelector(
    AiAlwaysOnAutomationsSelectors.getIsSocialEngagementPublished,
  )
  const instantAnswersPublished = useAppSelector(
    AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus,
  )
  const hasActiveGoals =
    useAppSelector(AiAutomationEngagementGoalSelectors.getActiveGoals).length > 0

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const toggleStatus = useCallback(async () => {
    if (
      scenario === AiAutomationScenario.SOCIAL_ENGAGEMENT &&
      socialEngagementPublished &&
      !instantAnswersPublished &&
      hasActiveGoals
    ) {
      setIsConfirmModalOpen(true)
      return
    }

    if (
      scenario === AiAutomationScenario.INSTANT_ANSWERS &&
      instantAnswersPublished &&
      !socialEngagementPublished &&
      hasActiveGoals
    ) {
      setIsConfirmModalOpen(true)
      return
    }

    const status =
      scenario === AiAutomationScenario.INSTANT_ANSWERS
        ? !instantAnswersPublished
        : !socialEngagementPublished

    await dispatch(
      AiAlwaysOnAutomationsActions.updateScenarioStatus({
        scenario,
        status,
      }),
    ).unwrap()

    AiAutomationAnalytics.logAutomationStatusChanged({
      type: scenario,
      is_published: status,
      source,
    })
  }, [
    scenario,
    socialEngagementPublished,
    instantAnswersPublished,
    hasActiveGoals,
    dispatch,
    source,
  ])

  const closeConfirmModal = useCallback(() => setIsConfirmModalOpen(false), [])

  return {
    toggleStatus,
    isConfirmModalOpen,
    closeConfirmModal,
  }
}
